import { useQuery } from "react-query";
import { fetchData } from "../api/client";
import { ACTIONS, DEFAULT_PERMISSION_CATALOG } from "./usePositions";
import {
  readActiveCompanySession,
  readSelectedCompanySession,
  getActiveWorkspaceSlug,
} from "../utils/workspaceStorage";

/**
 * Retorna { company, scopes: string[] } de /me/permissions
 * Revalida automaticamente quando a "Company" do header mudar.
 */
export function useEffectivePermissions() {
  const companyHeader =
    getActiveWorkspaceSlug() || readActiveCompanySession() || readSelectedCompanySession() || null;

  return useQuery(
    ["me-permissions", companyHeader],
    async () => {
      const res = await fetchData(`/me/permissions`, "GET");
      const data = res?.data || res || {};
      return {
        company: data.company ?? null,
        scopes: Array.isArray(data.scopes) ? data.scopes : [],
        features: data.features && typeof data.features === "object" ? data.features : {},
        assignablePositionIds: Array.isArray(data.assignablePositionIds)
          ? data.assignablePositionIds.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0)
          : data.assignablePositionIds === null
            ? null
            : [],
        blockedOutboundChannelIds: Array.isArray(data.blockedOutboundChannelIds)
          ? data.blockedOutboundChannelIds
              .map((id) => Number(id))
              .filter((n) => Number.isFinite(n) && n > 0)
          : data.blockedOutboundChannelIds === null
            ? null
            : [],
      };
    },
    {
      enabled: Boolean(companyHeader),
      refetchOnWindowFocus: false,
      staleTime: 10 * 60 * 1000,
    }
  );
}

/** Recurso e ação da permissão para Emitir BPe (passagens rodoviárias). Alinhado ao catálogo da API (bpe.view). */
export const BPE_PERMISSION_RESOURCE = "bpe";
export const BPE_PERMISSION_ACTION = "view";
export const BPE_SCOPE = `${BPE_PERMISSION_RESOURCE}.${BPE_PERMISSION_ACTION}`;

/** Ações de Meu Perfil liberadas quando o cargo ainda não tem nenhum profile.*. */
export const PROFILE_OPEN_BY_DEFAULT = new Set([
  "view",
  "update_identity",
  "update_preferences",
  "manage_notifications",
  "reset_password",
]);

/** Ações de Meu Perfil que exigem grant explícito no cargo. */
export const PROFILE_CLOSED_BY_DEFAULT = new Set([
  "connect_google_calendar",
  "connect_personal_whatsapp",
]);

function hasAnyProfileScope(set) {
  for (const s of set) {
    if (String(s).startsWith("profile.")) return true;
  }
  return false;
}

/** Soft-default de Meu Perfil (alinhado a xpro4api/src/utils/scopeCheck.js). */
export function canProfile(set, action = "view") {
  const act = String(action || "view");
  const key = `profile.${act}`;
  if (set.has(key)) return true;
  if (PROFILE_CLOSED_BY_DEFAULT.has(act)) return false;
  if (PROFILE_OPEN_BY_DEFAULT.has(act) && !hasAnyProfileScope(set)) return true;
  return false;
}

/** Helpers */
export const toSet = (arr) => new Set(Array.isArray(arr) ? arr : []);
export const hasScope = (set, scope) => set.has(scope);
export const can = (set, resource, action = "view") => {
  if (set.has(`${resource}.${action}`)) return true;
  if (resource === "profile") return canProfile(set, action);
  if (resource === "passagens" && action === "view" && set.has("bpe.view")) return true;
  if (resource === "passagens" && action === "export" && set.has("bpe.view")) return true;
  if (resource === "passagens" && action === "request_repasse" && set.has("passagens.view")) return true;
  if (resource === "passagens" && action === "request_repasse" && set.has("bpe.view")) return true;
  // Retrocompat: Cidades herdou de Produtos (menu/API) até o cargo ter cities.* explícito.
  if (resource === "cities") {
    if (action === "view" && set.has("products.view")) return true;
    if (action === "create" && set.has("products.create")) return true;
    if (action === "update" && set.has("products.update")) return true;
    if (action === "delete" && set.has("products.delete")) return true;
  }
  if (resource === "team_tasks") {
    if (action === "view" && (set.has("team_chat.view") || set.has("chat.view"))) return true;
    if (action === "manage" && set.has("team_chat.manage_channels")) return true;
  }
  // Retrocompat: Clientes só tinha clients.view e a tela sempre permitiu gestão.
  if (resource === "clients" && action === "manage" && set.has("clients.view")) return true;
  return false;
};
export const canAny = (set, pairs = []) =>
  pairs.some(([resource, action = "view"]) => set.has(`${resource}.${action}`));

/** Verifica se o usuário tem permissão para usar a funcionalidade de Emitir BPe no Chat. */
export const canEmitirBPe = (set) => can(set, BPE_PERMISSION_RESOURCE, BPE_PERMISSION_ACTION);

/** Expande catálogo (API ou fallback) em escopos `recurso.ação`. */
export function scopesFromPermissionCatalog(catalog) {
  const out = new Set();
  const cat = Array.isArray(catalog) && catalog.length ? catalog : DEFAULT_PERMISSION_CATALOG;
  for (const mod of cat) {
    for (const r of mod.resources || []) {
      const acts = Array.isArray(r.actions) && r.actions.length ? r.actions : ACTIONS;
      for (const a of acts) out.add(`${r.key}.${a}`);
    }
  }
  return out;
}

/** União de vários conjuntos de escopos (Set ou array de strings). */
export function unionScopeSets(...sets) {
  const out = new Set();
  for (const s of sets) {
    if (!s) continue;
    if (s instanceof Set) {
      for (const x of s) out.add(x);
    } else if (Array.isArray(s)) {
      for (const x of s) out.add(x);
    }
  }
  return out;
}

/**
 * Escopos efetivos para checagens de UI: usuário master recebe superset do catálogo padrão,
 * para não esconder menus/matriz quando `/me/permissions` ou cache estiver defasado.
 */
export function effectiveScopeSetForUser(scopesArray, { isMaster } = {}) {
  const base = toSet(scopesArray);
  if (!isMaster) return base;
  return unionScopeSets(base, scopesFromPermissionCatalog(DEFAULT_PERMISSION_CATALOG));
}

/** Conjunto de escopos para checagens de UI a partir de `/me/permissions` e userData. */
export function scopeSetFromPermissions(permissionsData, userData) {
  const scopes = effectiveScopeSetForUser(permissionsData?.scopes, {
    isMaster: Number(userData?.master) === 1,
  });
  // Gates de produto via API (`features.campaigns` / `features.paidAds`).
  // Sem isso, recursos ficam ocultos mesmo para master (catálogo fallback).
  if (permissionsData?.features?.campaigns !== true) {
    for (const scope of scopes) {
      if (scope.startsWith("campaigns.")) scopes.delete(scope);
    }
  }
  if (permissionsData?.features?.paidAds !== true) {
    for (const scope of scopes) {
      if (scope.startsWith("paid_ads.")) scopes.delete(scope);
    }
  }
  return scopes;
}
